//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useMemo, useRef } from 'react';

import { type PagePublication } from './contract';
import {
  type ActionHandler, PageSnapshotContext, type PageSnapshotContextValue
} from './PageSnapshotContext';

interface PageSnapshotProviderProps {
  children: React.ReactNode;
}

const PageSnapshotProvider = ({ children }: PageSnapshotProviderProps) => {
  const published = useRef<PagePublication | null>(null);
  const handlers = useRef<ReadonlyMap<string, ActionHandler>>(new Map());
  const listeners = useRef(new Set<() => void>());

  // Built once: every field reads or writes a ref, so the value never needs
  // to change identity, and a consumer never re-renders for a publish.
  const value = useMemo((): PageSnapshotContextValue => {
    const notify = () => { for (const listener of listeners.current) listener(); };
    return {
      getPublished: () => published.current,
      publish: (snapshot) => {
        published.current = snapshot;
        notify();
        return () => {
          if (published.current !== snapshot) return;
          published.current = null;
          notify();
        };
      },
      getHandlers: () => handlers.current,
      registerHandler: (key, handler) => {
        handlers.current = new Map(handlers.current).set(key, handler);
        notify();
        return () => {
          // Only the registration being undone may remove the entry: a page
          // that re-registered under the same key must not lose its handler
          // to the old registration's cleanup.
          if (handlers.current.get(key) !== handler) return;
          const next = new Map(handlers.current);
          next.delete(key);
          handlers.current = next;
          notify();
        };
      },
      subscribe: (listener) => {
        listeners.current.add(listener);
        return () => { listeners.current.delete(listener); };
      }
    };
  }, []);

  return (
    <PageSnapshotContext.Provider value={value}>
      {children}
    </PageSnapshotContext.Provider>
  );
};

export default PageSnapshotProvider;
